import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import OnOffToggle from './OnOffToggle';
import { Textarea } from './ui/textarea';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Plus, 
  Package, 
  Users,
  Upload,
  Eye,
  Trash2,
  X,
  Image as ImageIcon,
  FileText,
  Download,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { orgApi } from '../utils/apiV2';
import StandardModal from './StandardModal';
import { PrimaryButton, SecondaryButton } from './Buttons';
import RoleSelector from './RoleSelector';
import TeamMemberSelector from './TeamMemberSelector';
import { useRoles } from '../hooks/useData';
import useModalPersistence from '../hooks/useModalPersistence';

const AddAssetModal = ({ isOpen, onClose, onSuccess, teamMembers }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [nameAvailable, setNameAvailable] = useState(null);
  const { roles } = useRoles();
  
  // Refs for file inputs (proper React way instead of document.getElementById)
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  
  const initialData = {
    name: '',
    active: true,
    capacity: 12,
    teamRequirements: [],
    allowedTeamMembers: [],
    photos: [],
    documents: [],
    notes: ''
  };
  
  const { formData, setFormData, clearDraft } = useModalPersistence('draft_addAsset', initialData, isOpen);

  const totalSteps = 7; // Updated: 1=Name, 2=Team Req, 3=Team Access, 4=Photos, 5=Docs, 6=Notes, 7=Review

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If updating name, debounce check
    if (field === 'name' && value) {
      setNameAvailable(null);
      setCheckingName(true);
      
      if (window.assetNameCheckTimeout) {
        clearTimeout(window.assetNameCheckTimeout);
      }
      
      window.assetNameCheckTimeout = setTimeout(async () => {
        try {
          const response = await api.get(`/assets/check-name/${encodeURIComponent(value)}`);
          setNameAvailable(response.data.available);
        } catch (error) {
          console.error('Error checking asset name:', error);
          setNameAvailable(true); // Fail-open
        } finally {
          setCheckingName(false);
        }
      }, 500);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const currentPhotos = formData.photos || [];
    
    if (currentPhotos.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed per asset");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        
        const response = await api.post('/upload/asset-photo', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      updateFormData('photos', [...currentPhotos, ...uploadedUrls]);
      toast.success(`${files.length} photo(s) uploaded`);
      
      // Reset input to allow re-upload of same file
      if (e.target) e.target.value = '';
    } catch (error) {
      console.error("Error uploading photos:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };
  
  const handleDocumentUpload = async (e) => {
    
    const files = Array.from(e.target.files);
    
    if (files.length === 0) {
      return;
    }
    
    const currentDocuments = formData.documents || [];
    
    if (currentDocuments.length + files.length > 10) {
      toast.error("Maximum 10 documents allowed per asset");
      return;
    }
    
    setUploadingDocument(true);
    
    try {
      const uploadedDocs = [];
      
      for (const file of files) {
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        
        const response = await api.post('/upload/asset-document', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        
        uploadedDocs.push({
          filename: file.name,
          url: response.data.url,
          uploadedAt: new Date().toISOString(),
          size: file.size
        });
      }
      
      
      updateFormData('documents', [...currentDocuments, ...uploadedDocs]);
      
      toast.success(`${files.length} document(s) uploaded`);
      
      // Reset input to allow re-upload of same file
      if (e.target) e.target.value = '';
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingDocument(false);
    }
  };


  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          toast.error('Please enter asset name');
          return false;
        }
        if (!formData.capacity || formData.capacity < 1) {
          toast.error('Please enter valid capacity');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        capacity: parseInt(formData.capacity) || 12,
        active: formData.active,
        teamRequirements: formData.teamRequirements,
        allowedTeamMembers: formData.allowedTeamMembers,
        photos: formData.photos || [],
        documents: formData.documents || [],
        photoUrl: formData.photos && formData.photos.length > 0 ? formData.photos[0] : null,
        notes: formData.notes?.trim() || null
      };

      await orgApi.createAsset(payload);
      toast.success(`${formData.name} added successfully!`);
      
      // Notify setup guide of data change
      window.dispatchEvent(new Event('setupDataChanged'));
      
      clearDraft();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding asset:', error);
      
      let errorMessage = 'Failed to add asset';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Asset Details</h2>
              <p className="text-gray-600 text-sm mt-1">Basic information about your asset</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Asset Name <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    placeholder="e.g., Sea Breeze, Atlantic Explorer"
                    className="mt-1 pr-10"
                  />
                  {formData.name && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingName ? (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : nameAvailable === true ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : nameAvailable === false ? (
                        <X className="w-4 h-4 text-red-600" />
                      ) : null}
                    </div>
                  )}
                </div>
                {nameAvailable === false && (
                  <p className="text-xs text-red-600 mt-1">Asset name already exists</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="capacity">
                    <Users className="w-4 h-4 inline mr-1" />
                    Capacity *
                  </Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => updateFormData('capacity', parseInt(e.target.value) || 12)}
                    placeholder="12"
                    className="mt-1"
                  />
                </div>
                
                <div className="flex flex-col justify-center">
                  <Label className="mb-2">Status</Label>
                  <div className="flex items-center gap-2">
                    <OnOffToggle
                      value={formData.active}
                      onChange={(checked) => updateFormData('active', checked)}
                    />
                    <span className="text-sm">{formData.active ? 'Active' : 'Inactive'}</span>
                  </div>
                  {!formData.active && (
                    <p className="text-xs text-red-600 mt-1">Won't be assigned to trips</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Team Requirements</h2>
              <p className="text-gray-600 text-sm mt-1">What team roles are needed to operate this asset?</p>
            </div>

            {!roles || roles.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-sm text-yellow-800">Please create roles first before adding team requirements.</p>
              </div>
            ) : (
            <div className="space-y-4">
              {formData.teamRequirements.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500 mb-3">No team required (self-service asset)</p>
                  <SecondaryButton
                    onClick={() => {
                      updateFormData('teamRequirements', [{
                        roleId: '',
                        roleName: '',
                        quantity: 1
                      }]);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Team Requirement
                  </SecondaryButton>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.teamRequirements.map((requirement, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <RoleSelector
                        roles={roles}
                        value={requirement.roleId}
                        onChange={(roleId) => {
                          const role = roles.find(r => r.id === roleId);
                          const updated = [...formData.teamRequirements];
                          updated[index] = {
                            ...requirement,
                            roleId,
                            roleName: role?.name || ''
                          };
                          updateFormData('teamRequirements', updated);
                        }}
                        placeholder="Select role..."
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={requirement.quantity}
                        onChange={(e) => {
                          const updated = [...formData.teamRequirements];
                          updated[index].quantity = parseInt(e.target.value) || 1;
                          updateFormData('teamRequirements', updated);
                        }}
                        className="w-20"
                        placeholder="Qty"
                      />
                      <SecondaryButton
                        size="sm"
                        onClick={() => {
                          const updated = formData.teamRequirements.filter((_, i) => i !== index);
                          updateFormData('teamRequirements', updated);
                        }}
                        className="p-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </SecondaryButton>
                    </div>
                  ))}
                  
                  <SecondaryButton
                    onClick={() => {
                      updateFormData('teamRequirements', [...formData.teamRequirements, {
                        roleId: '',
                        roleName: '',
                        quantity: 1
                      }]);
                    }}
                    className="w-full"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Another Role
                  </SecondaryButton>
                </div>
              )}
            </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Team Access</h2>
              <p className="text-gray-600 text-sm mt-1">Restrict this asset to only certain teamMembers?</p>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-blue-800">
                We know certain assets require specific experience levels. You may prefer to limit access to experienced teamMembers.
              </p>
            </div>

            <div>
              {formData.allowedTeamMembers.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500 mb-3">All teamMembers can use this asset</p>
                  <div className="max-w-sm mx-auto">
                    <TeamMemberSelector
                      teamMembers={teamMembers}
                      value=""
                      onChange={(teamMemberId) => {
                        if (teamMemberId) {
                          updateFormData('allowedTeamMembers', [teamMemberId]);
                        }
                      }}
                      placeholder="Select teamMember to restrict..."
                      showUnassign={false}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                    {formData.allowedTeamMembers.map(teamMemberId => {
                      const teamMember = teamMembers.find(c => c.id === teamMemberId);
                      return teamMember ? (
                        <div key={teamMemberId} className="flex items-center gap-1 bg-white rounded px-2 py-1">
                          <span className="text-xs font-medium">{teamMember.name}</span>
                          <button
                            onClick={() => {
                              updateFormData('allowedTeamMembers', 
                                formData.allowedTeamMembers.filter(id => id !== teamMemberId));
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                  
                  <TeamMemberSelector
                    teamMembers={teamMembers.filter(c => !formData.allowedTeamMembers.includes(c.id))}
                    value=""
                    onChange={(teamMemberId) => {
                      if (teamMemberId && teamMemberId !== "none") {
                        updateFormData('allowedTeamMembers', [...formData.allowedTeamMembers, teamMemberId]);
                      }
                    }}
                    placeholder="Add another teamMember..."
                    showUnassign={false}
                  />
                  
                  <SecondaryButton
                    onClick={() => updateFormData('allowedTeamMembers', [])}
                    size="sm"
                    className="w-full"
                  >
                    Allow All TeamMembers
                  </SecondaryButton>
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ImageIcon className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Asset Photos (Optional)</h2>
              <p className="text-gray-600 text-sm mt-1">Upload up to 5 photos of {formData.name || 'this asset'}</p>
            </div>

            {/* Photos Grid */}
            {formData.photos && formData.photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                {formData.photos.map((url, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <img 
                      src={url} 
                      alt={`Asset ${index + 1}`} 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => {
                        updateFormData('photos', formData.photos.filter((_, i) => i !== index));
                        toast.success("Photo removed");
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            {(!formData.photos || formData.photos.length < 5) && (
              <div>
                <input
                  type="file"
                  id="asset-photos-add"
                  ref={photoInputRef}
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <SecondaryButton
                  type="button"
                  className="w-full"
                  disabled={uploadingPhoto}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {uploadingPhoto ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photos ({5 - (formData.photos?.length || 0)} remaining)
                    </>
                  )}
                </SecondaryButton>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  JPG, PNG, WEBP • Max 5 photos • 5MB each
                </p>
              </div>
            )}
            
            {(!formData.photos || formData.photos.length === 0) && (
              <div className="text-center py-8 text-gray-400">
                <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">No photos uploaded yet</p>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Document Photos (Optional)</h2>
              <p className="text-gray-600 text-sm mt-1">Photos of insurance, registration, certificates</p>
            </div>

            {/* Documents Grid (Photos of Documents) */}
            {formData.documents && formData.documents.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                {formData.documents.map((doc, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <img 
                      src={doc.url} 
                      alt={doc.filename}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                      {doc.filename}
                    </div>
                    <button
                      onClick={() => {
                        updateFormData('documents', formData.documents.filter((_, i) => i !== index));
                        toast.success("Document photo removed");
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            {(!formData.documents || formData.documents.length < 10) && (
              <div>
                <input
                  type="file"
                  id="asset-documents-add"
                  ref={documentInputRef}
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handleDocumentUpload(e);
                  }}
                  className="hidden"
                />
                <SecondaryButton
                  type="button"
                  className="w-full"
                  disabled={uploadingDocument}
                  onClick={() => documentInputRef.current?.click()}
                >
                  {uploadingDocument ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document Photos ({10 - (formData.documents?.length || 0)} remaining)
                    </>
                  )}
                </SecondaryButton>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Take photos of insurance, registration, certificates • Max 10
                </p>
              </div>
            )}
            
            {(!formData.documents || formData.documents.length === 0) && (
              <div className="text-center py-8 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">No document photos yet</p>
                <p className="text-xs mt-1">Take photos of your asset paperwork</p>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Upload className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Additional Details</h2>
              <p className="text-gray-600 text-sm mt-1">Optional notes about this asset</p>
            </div>

            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => updateFormData('notes', e.target.value)}
                placeholder="e.g., Under maintenance until Dec 15th"
                className="mt-1"
                rows={4}
              />
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Review</h2>
              <p className="text-gray-600 text-sm mt-1">Please review before adding {formData.name}</p>
            </div>

            <div className="space-y-3">
              {/* Basic Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Asset
                </h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Name:</span> {formData.name}</p>
                  <p><span className="font-medium">Capacity:</span> {formData.capacity}</p>
                  <p><span className="font-medium">Status:</span> {formData.active ? 'Active' : 'Inactive'}</p>
                </div>
              </div>

              {/* Team Requirements */}
              {formData.teamRequirements.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Required Team
                  </h3>
                  <div className="space-y-1 text-sm">
                    {formData.teamRequirements.map((req, idx) => (
                      <p key={idx}>{req.quantity} {req.roleName}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Allowed TeamMembers */}
              {formData.allowedTeamMembers.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Allowed Team ({formData.allowedTeamMembers.length})
                  </h3>
                  <div className="space-y-1 text-sm">
                    {formData.allowedTeamMembers.map(teamMemberId => {
                      const teamMember = teamMembers.find(c => c.id === teamMemberId);
                      return teamMember ? (
                        <p key={teamMemberId}>{teamMember.name}</p>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {/* Photos */}
              {formData.photos && formData.photos.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Photos ({formData.photos.length})
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {formData.photos.map((url, idx) => (
                      <img key={idx} src={url} alt={`Preview ${idx + 1}`} className="w-full aspect-square object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Documents */}
              {formData.documents && formData.documents.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documents ({formData.documents.length})
                  </h3>
                  <div className="space-y-1 text-sm">
                    {formData.documents.map((doc, idx) => (
                      <p key={idx}>• {doc.filename}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Asset"
      description={null}
      size="md"
      showCloseButton={false}
      footer={null}
    >
      {/* Visual Progress Bar */}
      <div className="relative h-2 bg-gray-200 rounded-full mb-8">
        <div 
          style={{ backgroundColor: '#2563EB' }}
          className="absolute h-full rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step Content */}
      <div className="min-h-[300px]">
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between gap-3 pt-4">
        {currentStep > 1 ? (
          <SecondaryButton onClick={() => setCurrentStep(prev => Math.max(prev - 1, 1))}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </SecondaryButton>
        ) : (
          <SecondaryButton onClick={onClose}>
            Cancel
          </SecondaryButton>
        )}
        
        {currentStep === totalSteps ? (
          <PrimaryButton onClick={handleSubmit}>
            <Check className="w-4 h-4 mr-1" />
            Add Asset
          </PrimaryButton>
        ) : (
          <PrimaryButton 
            onClick={() => {
              if (validateCurrentStep()) {
                setCurrentStep(prev => Math.min(prev + 1, totalSteps));
              }
            }}
            disabled={currentStep === 1 && (checkingName || nameAvailable !== true || !formData.name.trim())}
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </PrimaryButton>
        )}
      </div>
    </StandardModal>
  );
};

export default AddAssetModal;
